import { useCallback, useEffect, useState } from 'react';
import type React from 'react';

import { Button } from '@change/ds-core-ui';
import { SignaturesIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import {
	useMobileNativeShare,
	useShareCtaPlacementEnabled,
	useSignatureTracking,
} from '#src/app/pages/petitionDetails/shared/hooks';

import { ActionButtonWrapper } from './ActionButtonWrapper';
import type { ActionPanelVariant } from './MobileActionButton';
import { MobileSignModal, usePreloadMobileSignModal } from './MobileSignModal';
import { useMobileModalContext } from './shared/useMobileModalContext';
import { useShowSignForm } from './shared/useShowSignForm';
import { SignCtaShareAffordance } from './SignCtaShareAffordance';

type Props = {
	/**
	 * Whether this render site paints the share affordance.
	 */
	rendersShareAffordance?: boolean;
	sticky: boolean;
	visible: boolean;
	variant: ActionPanelVariant;
};

/**
 * Always stacks the share affordance's slot under the sign button, regardless of
 * `shareCtaPlacementEnabled` — with a single child, `gap-2` adds no spacing, so this is visually
 * identical to the bare button. `shareCtaPlacementEnabled` depends on `useIsMobile()`, which reports
 * `false` server-side on the guest, cached petition route (no session, so no real user agent to read)
 * and the real device type client-side — a wrapper that appeared only when that flag is true would
 * flip in and out across hydration and mismatch. See CHANGE-99549.
 */
function pickActionContent({
	shareAffordance,
	signButton,
}: {
	shareAffordance: React.ReactNode;
	signButton: React.ReactNode;
}): React.ReactNode {
	return (
		<div className="flex flex-col gap-2">
			{signButton}
			{shareAffordance}
		</div>
	);
}

export const MobileSignButton: React.FC<Props> = ({ rendersShareAffordance = true, sticky, visible, variant }) => {
	const [isOpen, setIsOpen] = useState(false);
	const { trackMobileSignIntent } = useSignatureTracking();

	const context = useMobileModalContext();

	usePreloadMobileSignModal();

	// `sticky` only names the tracking context here; it does not decide whether this instance renders
	// the affordance.
	const trackingContext = sticky ? 'petition_show_sign_cta_sticky' : 'petition_show_sign_cta_inline';

	const shareAction = useMobileNativeShare(trackingContext);
	const shareCtaPlacementEnabled = useShareCtaPlacementEnabled() && rendersShareAffordance;

	const openModal = useCallback(() => {
		trackMobileSignIntent();
		setIsOpen(true);
	}, [trackMobileSignIntent]);

	const openModalWithoutTracking = useCallback(() => {
		setIsOpen(true);
	}, []);

	const signButton = (
		<Button
			variant="primary"
			onClick={openModal}
			leadingIcon={<SignaturesIcon size="small" />}
			data-qa="mobile-sign-button"
			data-testid="mobile-sign-button"
			data-gtm="ga4-sign-button"
			size="large"
			className="w-full max-w-none"
		>
			<Translate value="corgi.petition_gamma.petition_sign.action" />
		</Button>
	);

	const shareAffordance = (
		<ClientRender>
			{shareCtaPlacementEnabled ? <SignCtaShareAffordance shareAction={shareAction} /> : null}
		</ClientRender>
	);

	const actionContent = pickActionContent({
		shareAffordance,
		signButton,
	});

	return (
		<>
			{/* we're looking at breakpoints to decide whether to open the modal, which cannot be done server-side */}
			{/* Only auto-open on the non-sticky instance to avoid opening modal twice (both sticky and non-sticky render this component) */}
			{!sticky && (
				<ClientRender>
					<MobileSignButtonAutoOpen onOpen={openModalWithoutTracking} />
				</ClientRender>
			)}
			{/* eslint-disable-next-line @change-corgi/require-data-qa-attribute */}
			<ActionButtonWrapper visible={visible} sticky={sticky}>
				{actionContent}
			</ActionButtonWrapper>

			<MobileSignModal context={context} isOpen={isOpen} setIsOpen={setIsOpen} variant={variant} />
		</>
	);
};
function MobileSignButtonAutoOpen({ onOpen }: { onOpen: () => void }) {
	const showSignForm = useShowSignForm();

	useEffect(() => {
		if (showSignForm) {
			/**
			 * There are some updates that happen right before showing the modal (such as populating the form fields)
			 * that need to complete first so that the modal opens with the correct data.
			 * Using setTimeout to defer the onOpen call to the next event loop iteration and avoid a race condition.
			 */
			setTimeout(() => {
				onOpen();
			}, 0);
		}
		// disabling dependency check to avoid attempting to open the modal multiple times
	}, []);

	return null;
}
